import { BRAND } from "@/constants";
import Box from "@mui/material/Box";
import { alpha, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { ReactNode } from "react";
import {
  AUTH_SHORT,
  AUTH_SHORTEST,
  AUTH_WIDE_TALL,
} from "./authSpacing";

// Logo height from md up; mirrored as a bottom spacer (except on short screens) so the
// content block centres on the whole panel (and lines up with the form).
const LOGO_MD = 72;
// Rhythm between headline block, steps and trust list: 16px short → 48px tall.
const SECTION_GAP = "clamp(16px, 4vh, 48px)";

interface AuthBrandPanelProps {
  headline: string;
  lede?: string;
  /** Rendered under the lede. Shown from md up only. */
  note?: ReactNode;
  /** Extra panel content (e.g. onboarding steps). Shown from md up only. */
  children?: ReactNode;
}

export default function AuthBrandPanel({
  headline,
  lede,
  note,
  children,
}: AuthBrandPanelProps) {
  const { palette } = useTheme();

  return (
    <Box
      component="aside"
      sx={{
        bgcolor: "secondary.main",
        backgroundImage: `radial-gradient(120% 70% at 100% 100%, ${alpha(
          palette.primary.light,
          0.55
        )} 0%, ${alpha(palette.secondary.main, 0)} 60%)`,
        color: "common.white",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: { xs: 3, sm: 2, md: 3 },
        px: { xs: 3, sm: 5, md: 6, lg: 8 },
        // Desktop vertical padding tracks viewport height (42px at 768 → 80px at 1440).
        py: { xs: 3, sm: 3, md: "clamp(32px, 5.5vh, 80px)" },
        "&::after": {
          content: '""',
          flexShrink: 0,
          height: LOGO_MD,
          // display, not height: a zero-height flex item would still take a gap.
          display: { xs: "none", md: "block" },
        },
        [AUTH_SHORT]: { gap: 2, "&::after": { display: "none" } },
        [AUTH_SHORTEST]: { gap: 1.5 },
      }}
    >
      {/* BRAND.logo is the white-on-dark variant for every brand (same as the sidebar). */}
      <Box
        component="img"
        src={BRAND.logo}
        alt={BRAND.name}
        sx={{
          display: "block",
          width: "auto",
          height: "auto",
          maxHeight: { xs: 40, sm: 56, md: LOGO_MD },
          [AUTH_SHORTEST]: { maxHeight: 56 },
          maxWidth: { xs: "60%", md: "80%" },
          objectFit: "contain",
          objectPosition: "left",
          alignSelf: "flex-start",
        }}
      />
      {/* One content block, centred in the space under the logo. Auto margins
          collapse to 0 when the panel is short, so nothing clips. */}
      <Box
        sx={{
          my: { md: "auto" },
          display: "flex",
          flexDirection: "column",
          gap: { xs: 3, md: SECTION_GAP },
          [AUTH_SHORTEST]: { gap: 2 },
        }}
      >
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            flexDirection: "column",
            gap: 1.5,
            // Cap line length so wide panels don't stretch the copy into one long line.
            maxWidth: "40rem",
          }}
        >
          <Typography
            variant="h2Semi"
            component="h2"
            sx={{
              color: "common.white",
              lineHeight: 1.25,
              textWrap: "balance",
              // 24"+ monitors: scale up so the panel doesn't read small (≈39px at 1680 → 44px at 1920+).
              [AUTH_WIDE_TALL]: { fontSize: "clamp(34px, 2.3vw, 44px)" },
            }}
          >
            {headline}
          </Typography>
          {lede && (
            <Typography
              variant="body1Reg"
              component="p"
              sx={{ color: alpha(palette.common.white, 0.8) }}
            >
              {lede}
            </Typography>
          )}
          {note && (
            // Tablets stack the panel above the form; skip the aside there to save height.
            <Box sx={{ display: { xs: "none", md: "block" } }}>{note}</Box>
          )}
        </Box>
        {children && (
          <Box sx={{ display: { xs: "none", md: "block" } }}>{children}</Box>
        )}
      </Box>
    </Box>
  );
}
